import { Grid, IconButton, Tooltip, styled } from "@mui/material";
import { GridColDef, getGridStringOperators } from "@mui/x-data-grid";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserAccounts } from "../../../graphql/types";

import { InfoIcon } from "@phosphor-icons/react/dist/csr/Info";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { DatagridEmptyState } from "../../../components/DatagridEmptyState/DatagridEmptyState";
import { Role } from "../../../constants/roles";
import { DeleteAccountUser } from "./DeleteAccountUser";
import { EditableSelectRoles } from "./EditableSelectRoles/EditableSelectRoles";
import { getFilterRolesOperator } from "./FilterRolesComponent";

const RedClearIcon = styled(XIcon)(({ theme }) => ({
  color: theme.palette.error.main,
}));

const customOperators = getGridStringOperators().filter(({ value }) =>
  ["equals", "contains", "startsWith"].includes(value)
);

const allUsersColumns: GridColDef[] = [
  {
    field: "urlName",
    headerName: "URL Name",
    editable: true,
    flex: 1,
    filterOperators: customOperators,
  },
  {
    field: "name",
    headerName: "Name",
    editable: true,
    flex: 1,
    filterOperators: customOperators,
  },
];

const EmptyRows = () => <DatagridEmptyState Icon={InfoIcon} text="No additional roles added" />;

type UserAccountsTableProps = {
  accounts: UserAccounts[];
  userName: string;
  userEmail: string;
  globalRoles?: Role[];
};

const UserAccountsTable = ({ accounts, userName, userEmail, globalRoles }: UserAccountsTableProps) => {
  const [selectedAccountToDelete, setSelectedAccountToDelete] = useState<{
    accountName: string;
    accountUrlName: string;
  } | null>(null);
  const navigate = useNavigate();
  const rows: Pick<UserAccounts, "id" | "urlName" | "name" | "roles">[] = [];
  accounts.forEach((account: UserAccounts, index) => {
    // user having global and account roles will have two rows and we can't have duplicate ids
    rows.push({
      id: account.urlName + account.name + index,
      urlName: account.urlName,
      name: account.name,
      roles: account.roles,
    });
  });
  const hasGlobalRoles = globalRoles && globalRoles.length > 0;

  const noGlobalRolesColumns: GridColDef = {
    field: "roles",
    headerName: hasGlobalRoles ? "Additional Roles" : "Roles",
    flex: 1.5,
    minWidth: 400,
    sortable: false,
    renderCell: (params) => {
      return (
        <EditableSelectRoles
          email={userEmail}
          roles={params.row.roles}
          urlName={params.row.urlName}
          globalRoles={globalRoles}
        />
      );
    },
    filterOperators: getFilterRolesOperator({ rowField: "roles" }),
  };

  const actionsColumn: GridColDef = {
    field: "actions",
    headerName: "Actions",
    width: 100,
    sortable: false,
    filterable: false,
    renderCell: (params) => {
      return (
        <Grid container sx={{ justifyContent: "flex-end", mr: 2 }}>
          {params.row.roles.length === 0 ? (
            <Grid>-</Grid>
          ) : (
            <Tooltip title="Delete account from this user">
              <IconButton
                onClick={() =>
                  setSelectedAccountToDelete({
                    accountUrlName: params.row.urlName,
                    accountName: params.row.name,
                  })
                }
                aria-label={`Delete from account ${params.row.name}`}
              >
                <RedClearIcon />
              </IconButton>
            </Tooltip>
          )}
        </Grid>
      );
    },
  };

  const isLastAccount = accounts.length === 1 && globalRoles?.length === 0;

  return (
    <>
      <DataGridPro
        slotProps={{
          root: { "data-testid": "accounts-table" },
        }}
        rows={rows}
        columns={[...allUsersColumns, noGlobalRolesColumns, actionsColumn]}
        disableRowSelectionOnClick
        hideFooterPagination
        disableColumnPinning
        showToolbar
        disableDensitySelector
        isCellEditable={() => false}
        slots={{ noRowsOverlay: EmptyRows }}
        hideFooter={rows.length === 0}
      />
      {selectedAccountToDelete ? (
        <DeleteAccountUser
          onConfirm={() => {
            setSelectedAccountToDelete(null);
            if (isLastAccount) {
              navigate("/admin?currentTab=users");
            }
          }}
          onClose={() => setSelectedAccountToDelete(null)}
          userName={userName}
          userEmail={userEmail}
          accountUrlName={selectedAccountToDelete.accountUrlName}
          accountName={selectedAccountToDelete.accountName}
          isLastAccount={isLastAccount}
        />
      ) : null}
    </>
  );
};

export { UserAccountsTable };
